import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/contexts/AuthContext";
const AlphaGate = lazy(() => import("./components/AlphaGate"));
import Index from "./pages/Index";
import AppLayout from "@/components/layout/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useGlobalErrorHandler } from "@/hooks/useGlobalErrorHandler";
import { useAnalytics } from "@/hooks/useAnalytics";

// Lazy load non-critical UI (toasts, cookie banner) to reduce initial JS
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import("./components/CookieBanner"));

// Lazy load secondary routes only (not the landing page for better LCP)
const NotFound = lazy(() => import("./pages/NotFound"));
const AuctionDetail = lazy(() => import("./pages/AuctionDetail"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Shards = lazy(() => import("./pages/Shards"));
const Market = lazy(() => import("./pages/Market"));
const MarketDetail = lazy(() => import("./pages/MarketDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AuctionsHistory = lazy(() => import("./pages/AuctionsHistory"));

// Dashboard routes
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout"));
const DashboardOverview = lazy(() => import("./pages/dashboard/DashboardOverview"));
const DashboardBids = lazy(() => import("./pages/dashboard/DashboardBids"));
const DashboardAuctions = lazy(() => import("./pages/dashboard/DashboardAuctions"));
const DashboardWins = lazy(() => import("./pages/dashboard/DashboardWins"));
const DashboardWatchlist = lazy(() => import("./pages/dashboard/DashboardWatchlist"));
const IntegrationDiscord = lazy(() => import("./pages/dashboard/IntegrationDiscord"));
const DashboardSettings = lazy(() => import("./pages/dashboard/DashboardSettings"));
const DashboardDeveloper = lazy(() => import("./pages/dashboard/DashboardDeveloper"));
const DeveloperToolDetail = lazy(() => import("./pages/dashboard/DeveloperToolDetail"));
const DashboardAlphaRequest = lazy(() => import("./pages/dashboard/DashboardAlphaRequest"));

// Admin panel routes
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const PanelOverview = lazy(() => import("./pages/panel/PanelOverview"));
const PanelUsers = lazy(() => import("./pages/panel/PanelUsers"));
const PanelApi = lazy(() => import("./pages/panel/PanelApi"));
const PanelStats = lazy(() => import("./pages/panel/PanelStats"));
const PanelDocs = lazy(() => import("./pages/panel/PanelDocs"));
const PanelDocEditor = lazy(() => import("./pages/panel/PanelDocEditor"));
const PanelBugs = lazy(() => import("./pages/panel/PanelBugs"));
const PanelTodos = lazy(() => import("./pages/panel/PanelTodos"));
const PanelRoles = lazy(() => import("./pages/panel/PanelRoles"));
const PanelLogs = lazy(() => import("./pages/panel/PanelLogs"));
const PanelAlpha = lazy(() => import("./pages/panel/PanelAlpha"));

const ExportDownload = lazy(() => import("./pages/ExportDownload"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
    },
  },
});

// Component to handle external redirect
const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
};

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Global error handler + analytics tracker wrapper
const GlobalErrorHandler = ({ children }: { children: React.ReactNode }) => {
  useGlobalErrorHandler();
  useAnalytics();
  return <>{children}</>;
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="dark" themes={["light", "dark", "op"]} disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
            </Suspense>
            <Suspense fallback={null}>
              <Sonner />
            </Suspense>
            <BrowserRouter>
              <GlobalErrorHandler>
                <Routes>
                  {/* Main App Layout */}
                  <Route element={<AppLayout />}>
                    <Route path="/" element={<Index />} />
                    <Route path="/auctions" element={<Index />} />
                    <Route
                      path="/shards"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Shards />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/market"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AlphaGate featureKey="market">
                            <Market />
                          </AlphaGate>
                        </Suspense>
                      }
                    />
                    <Route
                      path="/market/:material"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AlphaGate featureKey="market">
                            <MarketDetail />
                          </AlphaGate>
                        </Suspense>
                      }
                    />
                    <Route
                      path="/auctions/history"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AuctionsHistory />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/auctions/history/:auctionId"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AuctionDetail />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/auction/:auctionId"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AuctionDetail />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/player/:uuid"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PlayerProfile />
                        </Suspense>
                      }
                    />

                    {/* Dashboard nested inside AppLayout */}
                    <Route
                      path="/dash"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <DashboardLayout />
                        </Suspense>
                      }
                    >
                      <Route
                        index
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardOverview />
                          </Suspense>
                        }
                      />
                      <Route
                        path="bids"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardBids />
                          </Suspense>
                        }
                      />
                      <Route
                        path="auctions"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardAuctions />
                          </Suspense>
                        }
                      />
                      <Route
                        path="wins"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardWins />
                          </Suspense>
                        }
                      />
                      <Route
                        path="watchlist"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardWatchlist />
                          </Suspense>
                        }
                      />
                      <Route
                        path="integration/discord"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AlphaGate featureKey="discord_integration">
                              <IntegrationDiscord />
                            </AlphaGate>
                          </Suspense>
                        }
                      />
                      <Route
                        path="dev"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AlphaGate featureKey="developer_api">
                              <DashboardDeveloper />
                            </AlphaGate>
                          </Suspense>
                        }
                      />
                      <Route
                        path="dev/:toolId"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <AlphaGate featureKey="developer_api">
                              <DeveloperToolDetail />
                            </AlphaGate>
                          </Suspense>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardSettings />
                          </Suspense>
                        }
                      />
                      <Route
                        path="alpha"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <DashboardAlphaRequest />
                          </Suspense>
                        }
                      />
                    </Route>

                    {/* Admin Panel nested inside AppLayout */}
                    <Route
                      path="/panel"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AdminLayout />
                        </Suspense>
                      }
                    >
                      <Route
                        index
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelOverview />
                          </Suspense>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelUsers />
                          </Suspense>
                        }
                      />
                      <Route
                        path="api"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelApi />
                          </Suspense>
                        }
                      />
                      <Route
                        path="stats"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelStats />
                          </Suspense>
                        }
                      />
                      <Route
                        path="docs"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelDocs />
                          </Suspense>
                        }
                      />
                      <Route
                        path="docs/:docId"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelDocEditor />
                          </Suspense>
                        }
                      />
                      <Route
                        path="bugs"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelBugs />
                          </Suspense>
                        }
                      />
                      <Route
                        path="todos"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelTodos />
                          </Suspense>
                        }
                      />
                      <Route
                        path="roles"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelRoles />
                          </Suspense>
                        }
                      />
                      <Route
                        path="logs"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelLogs />
                          </Suspense>
                        }
                      />
                      <Route
                        path="alpha"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <PanelAlpha />
                          </Suspense>
                        }
                      />
                    </Route>
                  </Route>

                  {/* Export Download – auth-gated download page */}
                  <Route
                    path="/download"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <ExportDownload />
                      </Suspense>
                    }
                  />

                  {/* Redirect old /integration route to new location */}
                  <Route
                    path="/integration"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <IntegrationRedirect />
                      </Suspense>
                    }
                  />

                  {/* Redirect old /alpha route to new location */}
                  <Route
                    path="/alpha"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <AlphaRedirect />
                      </Suspense>
                    }
                  />

                  {/* Legal Pages */}
                  <Route
                    path="/privacy-policy"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <PrivacyPolicy />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/terms-of-service"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <TermsOfService />
                      </Suspense>
                    }
                  />

                  {/* /docs placeholder */}
                  <Route
                    path="/docs"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <DocsPlaceholder />
                      </Suspense>
                    }
                  />

                  {/* External redirects */}
                  <Route path="/impressum" element={<ExternalRedirect to="https://thael.group/imprint#opah" />} />
                  <Route path="/imprint" element={<ExternalRedirect to="https://thael.group/imprint#opah" />} />

                  {/* 404 */}
                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <NotFound />
                      </Suspense>
                    }
                  />
                </Routes>
                <Suspense fallback={null}>
                  <CookieBanner />
                </Suspense>
              </GlobalErrorHandler>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

// Redirect component for old /integration route
const IntegrationRedirect = () => {
  useEffect(() => {
    window.location.href = "/dash/integration/discord";
  }, []);
  return <PageLoader />;
};

// Redirect component for old /alpha route
const AlphaRedirect = () => {
  useEffect(() => {
    window.location.href = "/dash/alpha";
  }, []);
  return <PageLoader />;
};

// Docs placeholder page
const DocsPlaceholder = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-3 max-w-md px-4">
      <div className="p-3 rounded-xl bg-primary/10 w-fit mx-auto">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-primary"
        >
          <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
        </svg>
      </div>
      <h1 className="text-xl font-bold">Dokumentation</h1>
      <p className="text-sm text-muted-foreground">
        Die API-Dokumentation wird derzeit erstellt und ist bald verfügbar.
      </p>
      <a href="/" className="inline-block text-sm text-primary hover:underline">
        ← Zurück zur Startseite
      </a>
    </div>
  </div>
);

export default App;
